/**
 * Chart Export Utility
 * Exports chart data as CSV or chart visuals as PNG/PDF
 */
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Papa from 'papaparse';

// Types for chart data
export interface HourlyData {
  hour: string;
  hourNum: number;
  [deviceId: string]: string | number;
}

export interface WeeklyData {
  day: string;
  [deviceId: string]: string | number;
}

export interface DwellData {
  location: string;
  totalDuration: number;
  visitCount: number;
  [deviceId: string]: string | number;
}

export interface HeatmapCell {
  day: string;
  dayIndex: number;
  hour: number;
  total: number;
  byDevice: Record<string, number>;
}

export interface ChartExportData {
  hourlyData?: HourlyData[];
  weeklyData?: WeeklyData[];
  dwellData?: DwellData[];
  heatmapCells?: HeatmapCell[];
  deviceIds?: string[];
}

/**
 * Export hourly activity data to CSV
 */
export function exportHourlyDataToCSV(
  hourlyData: HourlyData[],
  deviceIds: string[],
  filename = 'hourly_activity'
): void {
  const rows = hourlyData.map(row => {
    const data: Record<string, string | number> = { Hour: row.hour };
    deviceIds.forEach(deviceId => {
      data[deviceId] = row[deviceId] || 0;
    });
    // Add total column
    data['Total'] = deviceIds.reduce((sum, id) => sum + ((row[id] as number) || 0), 0);
    return data;
  });

  const csv = Papa.unparse(rows);
  downloadFile(csv, `${filename}_${getTimestamp()}.csv`, 'text/csv');
}

/**
 * Export weekly activity data to CSV
 */
export function exportWeeklyDataToCSV(
  weeklyData: WeeklyData[],
  deviceIds: string[],
  filename = 'weekly_activity'
): void {
  const rows = weeklyData.map(row => {
    const data: Record<string, string | number> = { Day: row.day };
    deviceIds.forEach(deviceId => {
      data[deviceId] = row[deviceId] || 0;
    });
    // Add total column
    data['Total'] = deviceIds.reduce((sum, id) => sum + ((row[id] as number) || 0), 0);
    return data;
  });

  const csv = Papa.unparse(rows);
  downloadFile(csv, `${filename}_${getTimestamp()}.csv`, 'text/csv');
}

/**
 * Export dwell location data to CSV
 */
export function exportDwellDataToCSV(
  dwellData: DwellData[],
  deviceIds: string[],
  filename = 'dwell_locations'
): void {
  const rows = dwellData.map((row, idx) => {
    const data: Record<string, string | number> = {
      Rank: idx + 1,
      Location: row.location,
      'Total Duration (min)': row.totalDuration,
      'Visit Count': row.visitCount,
    };
    deviceIds.forEach(deviceId => {
      data[`${deviceId} (min)`] = row[deviceId] || 0;
    });
    return data;
  });

  const csv = Papa.unparse(rows);
  downloadFile(csv, `${filename}_${getTimestamp()}.csv`, 'text/csv');
}

/**
 * Export heatmap data to CSV (Hour x Day matrix)
 */
export function exportHeatmapToCSV(
  cells: HeatmapCell[],
  filename = 'activity_heatmap'
): void {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  // Create matrix: rows = hours, columns = days
  const rows = hours.map(hour => {
    const row: Record<string, string | number> = { 
      Hour: `${hour.toString().padStart(2, '0')}:00` 
    };
    days.forEach(day => {
      const cell = cells.find(c => c.hour === hour && c.day === day);
      row[day] = cell?.total || 0;
    });
    // Add row total
    row['Total'] = days.reduce((sum, day) => sum + ((row[day] as number) || 0), 0);
    return row;
  });

  // Add column totals row
  const totalsRow: Record<string, string | number> = { Hour: 'Total' };
  days.forEach(day => {
    totalsRow[day] = rows.reduce((sum, row) => sum + ((row[day] as number) || 0), 0);
  });
  totalsRow['Total'] = rows.reduce((sum, row) => sum + ((row['Total'] as number) || 0), 0);
  rows.push(totalsRow);

  const csv = Papa.unparse(rows);
  downloadFile(csv, `${filename}_${getTimestamp()}.csv`, 'text/csv');
}

/**
 * Export all chart data to a single CSV with multiple sections
 */
export function exportAllChartDataToCSV(
  data: ChartExportData,
  filename = 'chart_data_export'
): void {
  const sections: string[] = [];
  const deviceIds = data.deviceIds || [];

  // Heatmap section
  if (data.heatmapCells && data.heatmapCells.length > 0) {
    sections.push('=== ACTIVITY HEATMAP (Hour x Day) ===\n');
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const hours = Array.from({ length: 24 }, (_, i) => i);
    
    const heatmapRows = hours.map(hour => {
      const row: Record<string, string | number> = { 
        Hour: `${hour.toString().padStart(2, '0')}:00` 
      };
      days.forEach(day => {
        const cell = data.heatmapCells!.find(c => c.hour === hour && c.day === day);
        row[day] = cell?.total || 0;
      });
      return row;
    });
    sections.push(Papa.unparse(heatmapRows));
  }

  // Hourly data section
  if (data.hourlyData && data.hourlyData.length > 0) {
    sections.push('\n\n=== HOURLY ACTIVITY ===\n');
    const hourlyRows = data.hourlyData.map(row => {
      const result: Record<string, string | number> = { Hour: row.hour };
      deviceIds.forEach(id => { result[id] = row[id] || 0; });
      result['Total'] = deviceIds.reduce((sum, id) => sum + ((row[id] as number) || 0), 0);
      return result;
    });
    sections.push(Papa.unparse(hourlyRows));
  }

  // Weekly data section
  if (data.weeklyData && data.weeklyData.length > 0) {
    sections.push('\n\n=== WEEKLY PATTERN ===\n');
    const weeklyRows = data.weeklyData.map(row => {
      const result: Record<string, string | number> = { Day: row.day };
      deviceIds.forEach(id => { result[id] = row[id] || 0; });
      result['Total'] = deviceIds.reduce((sum, id) => sum + ((row[id] as number) || 0), 0);
      return result;
    });
    sections.push(Papa.unparse(weeklyRows));
  }

  // Dwell data section
  if (data.dwellData && data.dwellData.length > 0) {
    sections.push('\n\n=== TOP DWELL LOCATIONS ===\n');
    const dwellRows = data.dwellData.map((row, idx) => ({
      Rank: idx + 1,
      Location: row.location,
      'Total Duration (min)': row.totalDuration,
      'Visit Count': row.visitCount,
    }));
    sections.push(Papa.unparse(dwellRows));
  }

  const fullCsv = sections.join('');
  downloadFile(fullCsv, `${filename}_${getTimestamp()}.csv`, 'text/csv');
}

/**
 * Export a DOM element as PNG image
 */
export async function exportElementAsPNG(
  element: HTMLElement,
  filename = 'chart_export'
): Promise<void> {
  try {
    const canvas = await html2canvas(element, {
      backgroundColor: '#1a1a2e', // Dark background to match theme
      scale: 2, // Higher resolution
      logging: false,
      useCORS: true,
    });
    
    const dataUrl = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = `${filename}_${getTimestamp()}.png`;
    link.href = dataUrl;
    link.click();
  } catch (error) {
    console.error('Failed to export as PNG:', error);
    throw error;
  }
}

/**
 * Export a DOM element as PDF
 */
export async function exportElementAsPDF(
  element: HTMLElement,
  filename = 'chart_export',
  title = 'Chart Export'
): Promise<void> {
  try {
    const canvas = await html2canvas(element, {
      backgroundColor: '#1a1a2e',
      scale: 2,
      logging: false,
      useCORS: true,
    });
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const titleHeight = 15;
    
    // Add title
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 105, 148);
    pdf.text(title, margin, margin + 8);
    
    // Add timestamp
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, margin + 14);
    
    // Calculate image dimensions to fit page
    const availableWidth = pageWidth - (margin * 2);
    const availableHeight = pageHeight - margin - titleHeight - margin;
    
    const imgRatio = canvas.width / canvas.height;
    const pageRatio = availableWidth / availableHeight;
    
    let imgWidth, imgHeight;
    if (imgRatio > pageRatio) {
      imgWidth = availableWidth;
      imgHeight = availableWidth / imgRatio;
    } else {
      imgHeight = availableHeight;
      imgWidth = availableHeight * imgRatio;
    }
    
    // Center image
    const x = margin + (availableWidth - imgWidth) / 2;
    const y = margin + titleHeight;
    
    pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
    
    // Add footer
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);
    pdf.text('Generated by Pathfinder OSINT Explorer', pageWidth / 2, pageHeight - 5, { align: 'center' });
    
    pdf.save(`${filename}_${getTimestamp()}.pdf`);
  } catch (error) {
    console.error('Failed to export as PDF:', error);
    throw error;
  }
}

/**
 * Export multiple chart elements as a combined PDF report
 */
export async function exportChartsAsPDFReport(
  elements: { element: HTMLElement; title: string }[],
  filename = 'charts_report',
  reportTitle = 'Activity Charts Report'
): Promise<void> {
  try {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    
    // Title page
    pdf.setFillColor(0, 105, 148);
    pdf.rect(0, 0, pageWidth, 40, 'F');
    
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(255, 255, 255);
    pdf.text(reportTitle, margin, 25);
    
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, 35);
    
    pdf.setTextColor(33, 33, 33);
    
    let currentY = 50;
    
    for (let i = 0; i < elements.length; i++) {
      const { element, title } = elements[i];
      
      // Capture chart
      const canvas = await html2canvas(element, {
        backgroundColor: '#1a1a2e',
        scale: 2,
        logging: false,
        useCORS: true,
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Calculate dimensions
      const availableWidth = pageWidth - (margin * 2);
      const maxHeight = 100; // Max height per chart
      
      const imgRatio = canvas.width / canvas.height;
      let imgWidth = availableWidth;
      let imgHeight = availableWidth / imgRatio;
      
      if (imgHeight > maxHeight) {
        imgHeight = maxHeight;
        imgWidth = maxHeight * imgRatio;
      }
      
      // Check if we need a new page
      if (currentY + imgHeight + 20 > pageHeight - margin) {
        pdf.addPage();
        currentY = margin;
      }
      
      // Add chart title
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 105, 148);
      pdf.text(title, margin, currentY + 5);
      currentY += 10;
      
      // Add chart image
      const x = margin + (availableWidth - imgWidth) / 2;
      pdf.addImage(imgData, 'PNG', x, currentY, imgWidth, imgHeight);
      currentY += imgHeight + 15;
    }
    
    // Add page numbers
    const totalPages = pdf.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(128, 128, 128);
      pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
    }
    
    pdf.save(`${filename}_${getTimestamp()}.pdf`);
  } catch (error) {
    console.error('Failed to export PDF report:', error);
    throw error;
  }
}

/**
 * Helper: Download file to user's computer
 */
function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Helper: Get timestamp string for filenames
 */
function getTimestamp(): string {
  return new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
}

export default {
  exportHourlyDataToCSV,
  exportWeeklyDataToCSV,
  exportDwellDataToCSV,
  exportHeatmapToCSV,
  exportAllChartDataToCSV,
  exportElementAsPNG,
  exportElementAsPDF,
  exportChartsAsPDFReport,
};

